/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import ReactDOM from 'react-dom/client'
import { createHashRouter, RouterProvider, Navigate, useParams, createBrowserRouter } from 'react-router'
import Root from './root'
import OverviewRoute from './routes/overview'
import Index, { loader as indexLoader } from './routes/_index'
import { loader as browserLoader } from './routes/browser'
import { initializeI18n } from './services/i18n/i18n'
import i18nInstance from './services/i18n/i18n'
import { I18nextProvider } from 'react-i18next'
import { ROUTE_PATHS, APP_BASENAME } from './constants/routes'
import showModal from './contexts/ModalContext'

// Screens
import Notifications from './screens/notifications'
import Documents from './screens/documents'
import Tasks from './screens/tasks'
import Workflows from './screens/workflows'
import Etmf from './screens/etmf'
import StudyOverview from './screens/etmf/StudyOverview'
import Reports from './screens/reports'
import MyRaveRCMSettings from './screens/settings/MyRaveRCMSettings'
import Settings from './screens/settings'
import System from './screens/system'
import {
  RecentDocumentsPage,
  FavoritesPage,
  PeriodicReviewPage,
  SystemDocumentsPage,
  RegulatedContentPage,
  AllFoldersDocumentsPage
} from './pages/browser'
// Routes

import StagingLayout from './routes/staging'
import StagingIndexPage from './pages/staging/StagingIndexPage'
import StagingAreaPage from './pages/staging/StagingAreaPage'
import SiteDocsPage from './pages/staging/SiteDocsPage'
import PageStagingDocs from './pages/staging/PageStagingDocs'
import StagingOverviewPage from './pages/staging/overview'
import { Suspense } from 'react'
import LoadingSpinner from './components/LoadingSpinner'
import UserActivity from '~/screens/userActivity'
import StagingArea from './pages/staging/staging-area/StagingArea'
import { PropertySearchPage } from './pages/browser/PropertySearchPage'
import PageEtmfStatus from './pages/etmf/overview/Page'
import FsiActivity from './pages/browser/fsi-activity'
import SearchPage from './routes/search'
import checkmateService from './services/checkmate'
import EosOverviewPage from './pages/eos/overview/Page'

// Inside initApp or before router config
// 404 Route - Redirect to home
const NoMatch = () => {
  return <Navigate to="/" replace />
}

// Main app initialization
const initApp = async () => {
  await initializeI18n()
  // List of public routes that should not trigger protected API calls
  //Commented till we have a use for it
  // const PUBLIC_ROUTES: string[] = [ROUTE_PATHS.LOGIN]
  // const currentPath: string = window.location.pathname
  // let etmfFeature = false
  // let canAccessClinical = false
  // if (!PUBLIC_ROUTES.includes(currentPath)) {
  //   etmfFeature = await getFeatures().then((f) => f.etmfenabled)
  //   canAccessClinical = accessService.isAllowed('clinicalAccess')
  // }

  // Import getRole and USER_ROLES for role-based redirection
  const { getRole, USER_ROLES } = await import('./services/auth')

  // The staging route access control is handled within StagingLayout component

  // Dynamically select router type based on legacy logic: use hash router if window.MUI is defined, else browser router
  const isHashRouting = checkmateService.isCheckmated() ? true : false

  // If hash routing is enabled but URL has no hash, redirect to add hash before mounting router
  // This ensures the router sees the correct URL structure from the start
  if (isHashRouting) {
    const currentHash = window.location.hash
    if (!currentHash || currentHash === '#' || currentHash === '#/') {
      console.log('🔧 [Router Init] No valid hash detected, redirecting to add hash...')
      // Do a full page redirect to add hash to URL (query params must come BEFORE hash)
      // Trailing slash is needed: after basename '/!/app' is stripped from '#/!/app/', we get '/' which matches root route
      const searchParams = window.location.search
      const redirectUrl = `${window.location.origin}${window.location.pathname}${searchParams}#/!/app/`
      console.log('🔧 [Router Init] Redirecting to:', redirectUrl)

      // Commit the new hash, then reload once. A hash-only change on the same
      // pathname does not start a new document navigation, so the reload is
      // required to re-run the bootstrap against the new URL.
      window.location.replace(redirectUrl)
      window.location.reload()

      return // Stop execution - page will reload with hash
    }
    // Guard against malformed hashes that don't match the expected basename prefix.
    const hashPath = currentHash.startsWith('#') ? currentHash.slice(1) : currentHash
    if (!hashPath.includes('/app/')) {
      console.warn('🔧 [Router Init] Malformed hash detected:', currentHash, '→ redirecting to default route')
      const searchParams = window.location.search
      const redirectUrl = `${window.location.origin}${window.location.pathname}${searchParams}#/!/app/overview`
      window.location.replace(redirectUrl)
      window.location.reload()
      return
    }
    // Normalize legacy /Home → /overview in hash (email links use /Home with query params like ?taskId)
    if (/\/Home(\?|$)/.test(currentHash)) {
      const normalizedHash = currentHash.replace(/\/Home(\?|$)/, '/overview$1')
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${normalizedHash}`)
      console.log('🔧 [Router Init] Normalized legacy /Home → /overview in hash:', normalizedHash)
    }

    console.log('🔧 [Router Init] Hash is valid, proceeding with router setup')
  } else {
    // For non-checkmated (browser routing), ensure path starts with /app
    const currentPath = window.location.pathname

    // Special case: external apps may call these URLs directly - allow without redirect loop
    // - /Login or /login - login page
    // - /New-account/:hash - new account setup (external emails use capital N)
    // - /Reset-password/:hash - reset password with token (external emails use capital R)
    // - /forgot-password - forgot password page
    const isExternalLoginUrl = currentPath === '/Login' || currentPath === '/login'
    const isExternalNewAccountUrl = currentPath.startsWith('/New-account/') || currentPath.startsWith('/new-account/')
    const isExternalResetPasswordUrl =
      currentPath.startsWith('/Reset-password/') || currentPath.startsWith('/reset-password/')
    const isExternalForgotPasswordUrl = currentPath === '/forgot-password'
    const isExternalPublicUrl =
      isExternalLoginUrl || isExternalNewAccountUrl || isExternalResetPasswordUrl || isExternalForgotPasswordUrl

    if (!currentPath.startsWith('/app') && !isExternalPublicUrl) {
      console.log('🔧 [Router Init] Path does not start with /app, redirecting...')
      // Preserve the path as-is, just add /app prefix (keep original case)
      const pathWithoutLeadingSlash = currentPath.replace(/^\//, '')
      const searchParams = window.location.search
      const redirectUrl = `${window.location.origin}/app/${pathWithoutLeadingSlash}${searchParams}`
      console.log('🔧 [Router Init] Redirecting to:', redirectUrl)

      // Pathname differs from current document, so replace() triggers a real
      // navigation. Do NOT schedule a setTimeout reload here: the old document
      // keeps executing timers until the new response commits, and a reload
      // fired before then cancels the in-flight /app/ request and restarts
      // the redirect, producing a cancellation loop.
      window.location.replace(redirectUrl)

      return // Stop execution - page will navigate to correct path
    }

    if (isExternalPublicUrl && !currentPath.startsWith('/app')) {
      console.log('🔧 [Router Init] External public URL detected, adding /app prefix...')
      // For external URLs, add /app prefix and normalize path for router
      const searchParams = window.location.search
      let normalizedPath = currentPath

      // Normalize /New-account/:hash to /new-account/:hash (router uses lowercase)
      if (currentPath.startsWith('/New-account/')) {
        normalizedPath = currentPath.replace('/New-account/', '/new-account/')
      }

      const newUrl = `/app${normalizedPath}${searchParams}`
      console.log('🔧 [Router Init] Updating URL to:', newUrl)
      window.history.replaceState(null, '', newUrl)
    }

    // Normalize legacy /app/Home → /app/overview (email links use /Home with query params like ?taskId)
    if (currentPath === '/app/Home') {
      const searchParams = window.location.search
      window.history.replaceState(null, '', `/app/overview${searchParams}`)
      console.log('🔧 [Router Init] Normalized legacy /app/Home → /app/overview')
    }

    console.log('🔧 [Router Init] Path is valid, proceeding with router setup')
  }

  const createRouter = isHashRouting ? createHashRouter : createBrowserRouter
  const basename = isHashRouting ? '/!/app' : '/app'

  const router = createRouter(
    [
      {
        path: ROUTE_PATHS.ROOT,
        element: <Root />,
        children: [
          {
            index: true,
            element: <Index />, // Use Index component from _index.tsx
            loader: indexLoader // Attach loader for conditional navigation
          },
          { path: ROUTE_PATHS.OVERVIEW, element: <OverviewRoute /> },
          { path: ROUTE_PATHS.NOTIFICATIONS, element: <Notifications /> },
          { path: ROUTE_PATHS.TASKS.ROOT, element: <Tasks /> },
          { path: ROUTE_PATHS.ETMF.ROOT, element: <StudyOverview /> },
          {
            path: ROUTE_PATHS.ETMF.WITH_TAB,
            element: (
              <div className="mcc-content-frame theme-sandman">
                <div className="system-container">
                  <Suspense fallback={<LoadingSpinner />}>
                    <Etmf />
                  </Suspense>
                </div>
              </div>
            )
          },
          {
            path: 'show-file/:fileId',
            lazy: async () => {
              const { default: ShowFile, loader, ShowFileErrorState } = await import('./routes/show-file.$fileId')
              return { Component: ShowFile, loader, errorElement: <ShowFileErrorState /> }
            }
          },
          {
            path: 'show-file',
            lazy: async () => {
              const { default: ShowFile, loader } = await import('./routes/show-file')
              return { Component: ShowFile, loader }
            }
          },
          {
            path: 'show-file-qc/:fileId',
            lazy: async () => {
              const { default: ShowFileQC, loader, ErrorBoundary } = await import('./routes/show-file-qc.$fileId')
              return { Component: ShowFileQC, loader, ErrorBoundary }
            }
          },
          {
            path: 'show-file-diff/:file',
            lazy: async () => {
              const { default: ShowFileDiff, loader } = await import('./routes/show-file-diff.$file')
              return { Component: ShowFileDiff, loader }
            }
          },
          {
            path: 'show-sim-file',
            lazy: async () => {
              const { default: Component, meta } = await import('./routes/show-sim-file')
              return { Component, meta }
            }
          },
          {
            path: 'download-zip/:uuid',
            lazy: async () => {
              const { default: Component, meta } = await import('./routes/download-zip.$uuid')
              return { Component, meta }
            }
          },
          { path: ROUTE_PATHS.SETTINGS.ROOT, element: <MyRaveRCMSettings /> },
          { path: ROUTE_PATHS.SETTINGS.WITH_TAB, element: <Settings /> },
          {
            path: 'overview',
            element: <OverviewRoute />
          },
          {
            path: 'notifications',
            element: <Notifications />
          },
          {
            path: 'browser',
            element: <AllFoldersDocumentsPage />,
            loader: browserLoader,
            children: [
              {
                path: ':folderId',
                element: <AllFoldersDocumentsPage />
              }
            ]
          },
          {
            path: 'Search',
            element: <SearchPage />
          },
          {
            path: 'common-favorites',
            element: <FavoritesPage isCommonFav={true} />
          },
          {
            path: 'documents',
            element: <Documents />,
            children: [
              {
                path: 'recent',
                element: <RecentDocumentsPage />
              },
              {
                path: 'favorites',
                element: <FavoritesPage />
              },
              {
                path: 'periodic-review',
                element: <PeriodicReviewPage />
              },
              {
                path: 'system-documents',
                element: <SystemDocumentsPage />
              },
              {
                path: 'folder/:folderId',
                element: <RegulatedContentPage />
              },
              {
                path: 'property-search',
                element: <PropertySearchPage />
              }
            ]
          },
          {
            path: 'Tasks',
            element: <Tasks />,
            children: [
              {
                path: 'detail/:id',
                lazy: async () => {
                  const { default: TaskDetailsModalRoute } = await import('./routes/taskDetailsModalRoute')
                  return { Component: TaskDetailsModalRoute }
                }
              }
            ]
          },
          {
            path: 'Tasks/:tasksTab',
            element: <Tasks />
          },
          {
            path: 'ChangeRequests',
            element: <Workflows />,
            children: [
              {
                path: 'detail/:id',
                lazy: async () => {
                  const { default: WorkflowDetailsModalRoute } = await import('./routes/workflowDetailsModalRoute')
                  return { Component: WorkflowDetailsModalRoute }
                }
              }
            ]
          },
          {
            path: 'ChangeRequests/SubmittedWorkflowInstructions',
            lazy: async () => {
              const { default: PageSubmittedWorkflowRequests } = await import(
                './screens/submitted-workflow-requests/index'
              )
              return { Component: PageSubmittedWorkflowRequests }
            }
          },
          {
            path: 'ChangeRequests/:workflowsTab',
            element: <Workflows />
          },
          {
            path: 'clinical/',
            element: <StudyOverview />
          },
          {
            path: 'clinical/:etmfTab',
            element: <Etmf />
          },
          {
            path: 'reports',
            element: <Reports />,
            children: [
              {
                path: 'Activity',
                lazy: async () => {
                  const { ActivityPage } = await import('./screens/reports/activity')
                  return { Component: ActivityPage }
                }
              },
              {
                path: 'ClinicalActivity',
                lazy: async () => {
                  const { default: PageReportClinicalActivity } = await import(
                    './screens/reports/clinical-activity/index'
                  )
                  return { Component: PageReportClinicalActivity }
                }
              },
              {
                path: 'AccessRights',
                lazy: async () => {
                  const { default: PageReportsAcls } = await import('./screens/reports/acls/Page')
                  return { Component: PageReportsAcls }
                }
              },
              {
                path: 'Training',
                lazy: async () => {
                  const { default: PageReportsReadAcknowledge } = await import(
                    './screens/reports/read-acknowledge/Page'
                  )
                  return { Component: PageReportsReadAcknowledge }
                }
              },
              {
                path: 'Users',
                lazy: async () => {
                  const { default: PageReportsUsersUsage } = await import('./screens/reports/users-usage/Page')
                  return { Component: PageReportsUsersUsage }
                }
              },
              {
                path: 'GroupMembership',
                lazy: async () => {
                  const { default: PageReportsGroupMembership } = await import(
                    './screens/reports/group-membership/Page'
                  )
                  return { Component: PageReportsGroupMembership }
                }
              },
              {
                path: 'ChangeRequests',
                lazy: async () => {
                  const { default: PageReportsWorkflows } = await import('./screens/reports/workflows/Page')
                  return { Component: PageReportsWorkflows }
                }
              },
              {
                path: 'ChangeRequestsTypes',
                lazy: async () => {
                  const { default: PageReportsWorkflowTemplates } = await import(
                    './screens/reports/workflow-templates/Page'
                  )
                  return { Component: PageReportsWorkflowTemplates }
                }
              },
              {
                path: 'PropertiesUsage',
                lazy: async () => {
                  const { default: PageReportsPropertiesUsage } = await import(
                    './screens/reports/properties-usage/Page'
                  )
                  return { Component: PageReportsPropertiesUsage }
                }
              },
              {
                path: 'Tasks',
                lazy: async () => {
                  const { default: PageReportsTasks } = await import('./screens/reports/tasks/Page')
                  return { Component: PageReportsTasks }
                }
              },
              {
                path: 'Rejections',
                lazy: async () => {
                  const { default: PageReportsRejectionReasons } = await import(
                    './screens/reports/rejection-reasons/Page'
                  )
                  return { Component: PageReportsRejectionReasons }
                }
              },
              {
                path: 'DocExpiration',
                lazy: async () => {
                  const { default: PageReportsDocExpiration } = await import('./screens/reports/doc-expiration/Page')
                  return { Component: PageReportsDocExpiration }
                }
              },
              {
                path: 'Custom',
                lazy: async () => {
                  const { default: PageReportsCustom } = await import('./screens/reports/custom/Page')
                  return { Component: PageReportsCustom }
                }
              },
              {
                path: 'eTMFCompletion',
                lazy: async () => {
                  const { default: EtmfCompletionRatePage } = await import('./screens/reports/etmf-completion-rate')
                  return { Component: EtmfCompletionRatePage }
                }
              },
              {
                path: 'eTMFContributorDocStatus',
                lazy: async () => {
                  const { default: EtmfContributorDocStatusPage } = await import(
                    './screens/reports/etmf-contributor-doc-status'
                  )
                  return { Component: EtmfContributorDocStatusPage }
                }
              },
              {
                path: 'eTMFDocDetail',
                lazy: async () => {
                  const { default: EtmfDocDetailsPage } = await import('./screens/reports/etmf-doc-details')
                  return { Component: EtmfDocDetailsPage }
                }
              },
              {
                path: 'eTMFFiledDocs',
                lazy: async () => {
                  const { default: EtmfFiledDocsPage } = await import('./screens/reports/etmf-filed-docs')
                  return { Component: EtmfFiledDocsPage }
                }
              },
              {
                path: 'PropertySearch',
                lazy: async () => {
                  const { default: ReportPropertySearch } = await import('./screens/reports/property-search/index')
                  return { Component: ReportPropertySearch }
                }
              },
              {
                path: 'QcReview',
                lazy: async () => {
                  const { default: QCReviewReport } = await import('./screens/reports/qc-review/index')
                  return { Component: QCReviewReport }
                }
              },
              {
                path: 'duplicateDocuments',
                lazy: async () => {
                  const { default: PageReportDuplicateDocument } = await import(
                    './screens/reports/duplicate-documents/index'
                  )
                  return { Component: PageReportDuplicateDocument }
                }
              },
              {
                path: 'eTMFTimeliness',
                lazy: async () => {
                  const { default: PageReportETMFTimeliness } = await import('./screens/reports/etmf-timeliness/index')
                  return { Component: PageReportETMFTimeliness }
                }
              },
              {
                path: 'EosWorkflows',
                lazy: async () => {
                  const { default: EosWorkflowsPage } = await import('./screens/reports/eos-workflows/Page')
                  return { Component: EosWorkflowsPage }
                }
              },
              {
                path: 'EosWorkflowsRequest',
                lazy: async () => {
                  const { default: EosWorkflowsPage } = await import('./screens/reports/eos-workflows/Page')
                  return { Component: EosWorkflowsPage }
                }
              },
              {
                path: 'EosDownloadActivity',
                lazy: async () => {
                  const { default: EosDownloadActivityPage } = await import(
                    './screens/reports/eos-download-activity/Page'
                  )
                  return { Component: EosDownloadActivityPage }
                }
              },
              {
                path: 'EosAcknowledgementActivity',
                lazy: async () => {
                  const { default: EosAcknowledgementActivityPage } = await import(
                    './screens/reports/eos-acknowledgement-activity/Page'
                  )
                  return { Component: EosAcknowledgementActivityPage }
                }
              }
            ]
          },
          {
            path: 'settings',
            element: <MyRaveRCMSettings />
          },
          {
            path: 'settings/:settingsTab',
            element: <Settings />
          },
          {
            path: 'settings/activity',
            lazy: async () => {
              const { ActivityPage } = await import('./screens/reports/activity')
              return { Component: ActivityPage }
            }
          },
          {
            path: 'MySavedReportSettings',
            lazy: async () => {
              const { default: PageSavedReportSetting } = await import('./screens/savedReportSetting/Page')
              const { loader } = await import('./routes/saved-report-settings')
              return { Component: PageSavedReportSetting, loader }
            }
          },
          {
            path: 'system',
            element: <System />,
            children: [
              {
                index: true,
                lazy: async () => {
                  const { default: SystemInformation } = await import('./screens/system/SystemInformation')
                  return { Component: SystemInformation }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.PERFORMANCE,
                lazy: async () => {
                  const { default: SystemInformation } = await import('./screens/system/SystemInformation')
                  return { Component: SystemInformation }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.POLICIES,
                lazy: async () => {
                  const { default: Policies } = await import('./screens/system/policies')
                  return { Component: Policies }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.ETMF_SETUP,
                lazy: async () => {
                  const { default: ClinicalSetup } = await import('./screens/system/clinical-setup')
                  return { Component: ClinicalSetup }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.TEXT_MESSAGES,
                lazy: async () => {
                  const { default: TextMessages } = await import('./screens/system/text-messages')
                  return { Component: TextMessages }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.INTEGRATIONS,
                lazy: async () => {
                  const { default: Integrations } = await import('./screens/system/integrations')
                  return { Component: Integrations }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.OPERATING_MODE,
                lazy: async () => {
                  const { default: OperatingMode } = await import('./screens/system/operating-mode')
                  return { Component: OperatingMode }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.USERS,
                lazy: async () => {
                  const { default: Users } = await import('./screens/system/users')
                  return { Component: Users }
                },
                children: [
                  {
                    path: 'detail/:id',
                    lazy: async () => {
                      const { default: UserDetail } = await import('./routes/userDetailsModal')
                      return { Component: UserDetail }
                    }
                  }
                ]
              },
              {
                path: ROUTE_PATHS.SYSTEM.GROUPS,
                lazy: async () => {
                  const { default: Groups } = await import('./screens/system/groups')
                  return { Component: Groups }
                },
                children: [
                  {
                    path: 'detail/:id',
                    lazy: async () => {
                      const { default: GroupDetail } = await import('./routes/groupDetailsModal')
                      return { Component: GroupDetail }
                    }
                  }
                ]
              }
            ]
          },
          {
            path: 'staging',
            element: <StagingLayout />,
            children: [
              { index: true, element: <Navigate to="overview" replace /> },
              { path: 'overview', element: <StagingOverviewPage /> },
              { path: 'MissingDocuments', element: <PageStagingDocs key="MissingDocuments" /> },
              { path: 'MyDocuments', element: <PageStagingDocs key="MyDocuments" /> },
              { path: 'Documents', element: <SiteDocsPage /> },
              { path: 'StudyDocuments', element: <PageStagingDocs key="StudyDocuments" /> },
              { path: 'property-search', element: <PageStagingDocs key="property-search" /> },
              { path: 'stagingarea', element: <StagingArea /> }
              // { path: 'upload-docs', element: <UploadDocsPage /> } //update doc modal (no mapping required?)
            ]
          },
          {
            path: 'users/:userId/activity',
            element: (
              <div className="mcc-content-frame theme-sandman">
                <div className="system-container">
                  <Suspense fallback={<LoadingSpinner />}>
                    <UserActivity />
                  </Suspense>
                </div>
              </div>
            )
          },
          //FsiActivity/1763601
          {
            path: 'FsiActivity/:id',
            element: <FsiActivity />
          },

          {
            path: 'show-file',
            lazy: async () => {
              const { default: ShowFile, loader } = await import('./routes/show-file')
              return { Component: ShowFile, loader }
            }
          },
          { path: ROUTE_PATHS.SETTINGS.ROOT, element: <MyRaveRCMSettings /> },
          { path: ROUTE_PATHS.SETTINGS.WITH_TAB, element: <Settings /> },
          {
            path: 'overview',
            element: <OverviewRoute />
          },
          {
            path: 'notifications',
            element: <Notifications />
          },
          {
            path: 'browser',
            element: <AllFoldersDocumentsPage />,
            loader: browserLoader,
            children: [
              {
                path: ':folderId',
                element: <AllFoldersDocumentsPage />
              }
            ]
          },
          {
            path: 'Search',
            element: <SearchPage />
          },
          {
            path: 'upload-and-file/:parentId',
            lazy: async () => {
              const { default: PageUploadAndFile } = await import('./pages/browser/upload-and-file/PageUploadAndFile')
              return { Component: PageUploadAndFile }
            }
          },
          {
            path: 'upload-and-file',
            lazy: async () => {
              const { default: PageUploadAndFile } = await import('./pages/browser/upload-and-file/PageUploadAndFile')
              return { Component: PageUploadAndFile }
            }
          },
          {
            path: 'documents',
            element: <Documents />,
            children: [
              {
                path: 'recent',
                element: <RecentDocumentsPage />
              },
              {
                path: 'favorites',
                element: <FavoritesPage />
              },
              {
                path: 'periodic-review',
                element: <PeriodicReviewPage />
              },
              {
                path: 'system-documents',
                element: <SystemDocumentsPage />
              },
              {
                path: 'folder/:folderId',
                element: <RegulatedContentPage />
              },
              {
                path: 'property-search',
                element: <PropertySearchPage />
              }
            ]
          },
          {
            path: 'Tasks/:tasksTab',
            element: <Tasks />
          },
          {
            path: 'ChangeRequests/:workflowsTab',
            element: <Workflows />
          },
          {
            path: 'clinical/',
            element: <StudyOverview />
          },
          {
            path: 'clinical/:etmfTab',
            element: <Etmf />
          },
          {
            path: 'reports',
            element: <Reports />,
            children: [
              {
                path: 'Activity',
                lazy: async () => {
                  const { ActivityPage } = await import('./screens/reports/activity')
                  return { Component: ActivityPage }
                }
              },
              {
                path: 'ClinicalActivity',
                lazy: async () => {
                  const { default: PageReportClinicalActivity } = await import(
                    './screens/reports/clinical-activity/index'
                  )
                  return { Component: PageReportClinicalActivity }
                }
              },
              {
                path: 'AccessRights',
                lazy: async () => {
                  const { default: PageReportsAcls } = await import('./screens/reports/acls/Page')
                  return { Component: PageReportsAcls }
                }
              },
              {
                path: 'Training',
                lazy: async () => {
                  const { default: PageReportsReadAcknowledge } = await import(
                    './screens/reports/read-acknowledge/Page'
                  )
                  return { Component: PageReportsReadAcknowledge }
                }
              },
              {
                path: 'Users',
                lazy: async () => {
                  const { default: PageReportsUsersUsage } = await import('./screens/reports/users-usage/Page')
                  return { Component: PageReportsUsersUsage }
                }
              },
              {
                path: 'GroupMembership',
                lazy: async () => {
                  const { default: PageReportsGroupMembership } = await import(
                    './screens/reports/group-membership/Page'
                  )
                  return { Component: PageReportsGroupMembership }
                }
              },
              {
                path: 'ChangeRequests',
                lazy: async () => {
                  const { default: PageReportsWorkflows } = await import('./screens/reports/workflows/Page')
                  return { Component: PageReportsWorkflows }
                }
              },
              {
                path: 'ChangeRequestsTypes',
                lazy: async () => {
                  const { default: PageReportsWorkflowTemplates } = await import(
                    './screens/reports/workflow-templates/Page'
                  )
                  return { Component: PageReportsWorkflowTemplates }
                }
              },
              {
                path: 'PropertiesUsage',
                lazy: async () => {
                  const { default: PageReportsPropertiesUsage } = await import(
                    './screens/reports/properties-usage/Page'
                  )
                  return { Component: PageReportsPropertiesUsage }
                }
              },
              {
                path: 'Tasks',
                lazy: async () => {
                  const { default: PageReportsTasks } = await import('./screens/reports/tasks/Page')
                  return { Component: PageReportsTasks }
                }
              },
              {
                path: 'Rejections',
                lazy: async () => {
                  const { default: PageReportsRejectionReasons } = await import(
                    './screens/reports/rejection-reasons/Page'
                  )
                  return { Component: PageReportsRejectionReasons }
                }
              },
              {
                path: 'DocExpiration',
                lazy: async () => {
                  const { default: PageReportsDocExpiration } = await import('./screens/reports/doc-expiration/Page')
                  return { Component: PageReportsDocExpiration }
                }
              },
              {
                path: 'Custom',
                lazy: async () => {
                  const { default: PageReportsCustom } = await import('./screens/reports/custom/Page')
                  return { Component: PageReportsCustom }
                }
              },
              {
                path: 'eTMFCompletion',
                lazy: async () => {
                  const { default: EtmfCompletionRatePage } = await import('./screens/reports/etmf-completion-rate')
                  return { Component: EtmfCompletionRatePage }
                }
              },
              {
                path: 'eTMFContributorDocStatus',
                lazy: async () => {
                  const { default: EtmfContributorDocStatusPage } = await import(
                    './screens/reports/etmf-contributor-doc-status'
                  )
                  return { Component: EtmfContributorDocStatusPage }
                }
              },
              {
                path: 'eTMFDocDetail',
                lazy: async () => {
                  const { default: EtmfDocDetailsPage } = await import('./screens/reports/etmf-doc-details')
                  return { Component: EtmfDocDetailsPage }
                }
              },
              {
                path: 'eTMFFiledDocs',
                lazy: async () => {
                  const { default: EtmfFiledDocsPage } = await import('./screens/reports/etmf-filed-docs')
                  return { Component: EtmfFiledDocsPage }
                }
              },
              {
                path: 'PropertySearch',
                lazy: async () => {
                  const { default: ReportPropertySearch } = await import('./screens/reports/property-search/index')
                  return { Component: ReportPropertySearch }
                }
              },
              {
                path: 'QcReview',
                lazy: async () => {
                  const { default: QCReviewReport } = await import('./screens/reports/qc-review/index')
                  return { Component: QCReviewReport }
                }
              },
              {
                path: 'duplicateDocuments',
                lazy: async () => {
                  const { default: PageReportDuplicateDocument } = await import(
                    './screens/reports/duplicate-documents/index'
                  )
                  return { Component: PageReportDuplicateDocument }
                }
              }
            ]
          },
          {
            path: 'settings',
            element: <MyRaveRCMSettings />
          },
          {
            path: 'settings/:settingsTab',
            element: <Settings />
          },
          {
            path: 'settings/activity',
            lazy: async () => {
              const { ActivityPage } = await import('./screens/reports/activity')
              return { Component: ActivityPage }
            }
          },
          {
            path: 'MySavedReportSettings',
            lazy: async () => {
              const { default: PageSavedReportSetting } = await import('./screens/savedReportSetting/Page')
              const { loader } = await import('./routes/saved-report-settings')
              return { Component: PageSavedReportSetting, loader }
            }
          },
          {
            path: 'system',
            element: <System />,
            children: [
              {
                index: true,
                lazy: async () => {
                  const { default: SystemInformation } = await import('./screens/system/SystemInformation')
                  return { Component: SystemInformation }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.PERFORMANCE,
                lazy: async () => {
                  const { default: SystemInformation } = await import('./screens/system/SystemInformation')
                  return { Component: SystemInformation }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.POLICIES,
                lazy: async () => {
                  const { default: Policies } = await import('./screens/system/policies')
                  return { Component: Policies }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.ETMF_SETUP,
                lazy: async () => {
                  const { default: ClinicalSetup } = await import('./screens/system/clinical-setup')
                  return { Component: ClinicalSetup }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.TEXT_MESSAGES,
                lazy: async () => {
                  const { default: TextMessages } = await import('./screens/system/text-messages')
                  return { Component: TextMessages }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.INTEGRATIONS,
                lazy: async () => {
                  const { default: Integrations } = await import('./screens/system/integrations')
                  return { Component: Integrations }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.OPERATING_MODE,
                lazy: async () => {
                  const { default: OperatingMode } = await import('./screens/system/operating-mode')
                  return { Component: OperatingMode }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.USERS,
                lazy: async () => {
                  const { default: Users } = await import('./screens/system/users')
                  return { Component: Users }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.GROUPS,
                lazy: async () => {
                  const { default: Groups } = await import('./screens/system/groups')
                  return { Component: Groups }
                }
              }
            ]
          },
          {
            path: 'staging',
            element: <StagingLayout />,
            children: [
              { index: true, element: <Navigate to="overview" replace /> },
              { path: 'overview', element: <StagingOverviewPage /> },
              { path: 'MissingDocuments', element: <PageStagingDocs key="MissingDocuments" /> },
              { path: 'MyDocuments', element: <PageStagingDocs key="MyDocuments" /> },
              { path: 'Documents', element: <SiteDocsPage /> },
              { path: 'StudyDocuments', element: <PageStagingDocs key="StudyDocuments" /> },
              { path: 'property-search', element: <PageStagingDocs key="property-search" /> },
              { path: 'stagingarea', element: <StagingArea /> }
              // { path: 'upload-docs', element: <UploadDocsPage /> } //update doc modal (no mapping required?)
            ]
          },
          {
            path: 'users/:userId/activity',
            element: (
              <div className="mcc-content-frame theme-sandman">
                <div className="system-container">
                  <Suspense fallback={<LoadingSpinner />}>
                    <UserActivity />
                  </Suspense>
                </div>
              </div>
            )
          },
          {
            path: 'TrialOverview/status',
            element: <PageEtmfStatus />
          },
          //FsiActivity/1763601
          {
            path: 'FsiActivity/:id',
            element: <FsiActivity />
          },
          {
            path: 'show-file',
            lazy: async () => {
              const { default: ShowFile, loader } = await import('./routes/show-file')
              return { Component: ShowFile, loader }
            }
          },
          { path: ROUTE_PATHS.SETTINGS.ROOT, element: <MyRaveRCMSettings /> },
          { path: ROUTE_PATHS.SETTINGS.WITH_TAB, element: <Settings /> },
          {
            path: 'overview',
            element: <OverviewRoute />
          },
          {
            path: 'notifications',
            element: <Notifications />
          },
          {
            path: 'browser',
            element: <AllFoldersDocumentsPage />,
            loader: browserLoader,
            children: [
              {
                path: ':folderId',
                element: <AllFoldersDocumentsPage />
              }
            ]
          },
          {
            path: 'Search',
            element: <SearchPage />
          },
          {
            path: 'documents',
            element: <Documents />,
            children: [
              {
                path: 'recent',
                element: <RecentDocumentsPage />
              },
              {
                path: 'favorites',
                element: <FavoritesPage />
              },
              {
                path: 'periodic-review',
                element: <PeriodicReviewPage />
              },
              {
                path: 'system-documents',
                element: <SystemDocumentsPage />
              },
              {
                path: 'folder/:folderId',
                element: <RegulatedContentPage />
              },
              {
                path: 'property-search',
                element: <PropertySearchPage />
              }
            ]
          },
          {
            path: 'Tasks/:tasksTab',
            element: <Tasks />
          },
          {
            path: 'ChangeRequests/:workflowsTab',
            element: <Workflows />
          },
          {
            path: 'clinical/',
            element: <StudyOverview />
          },
          {
            path: 'clinical/:etmfTab',
            element: <Etmf />
          },
          {
            path: 'reports',
            element: <Reports />,
            children: [
              {
                path: 'Activity',
                lazy: async () => {
                  const { ActivityPage } = await import('./screens/reports/activity')
                  return { Component: ActivityPage }
                }
              },
              {
                path: 'ClinicalActivity',
                lazy: async () => {
                  const { default: PageReportClinicalActivity } = await import(
                    './screens/reports/clinical-activity/index'
                  )
                  return { Component: PageReportClinicalActivity }
                }
              },
              {
                path: 'AccessRights',
                lazy: async () => {
                  const { default: PageReportsAcls } = await import('./screens/reports/acls/Page')
                  return { Component: PageReportsAcls }
                }
              },
              {
                path: 'Training',
                lazy: async () => {
                  const { default: PageReportsReadAcknowledge } = await import(
                    './screens/reports/read-acknowledge/Page'
                  )
                  return { Component: PageReportsReadAcknowledge }
                }
              },
              {
                path: 'Users',
                lazy: async () => {
                  const { default: PageReportsUsersUsage } = await import('./screens/reports/users-usage/Page')
                  return { Component: PageReportsUsersUsage }
                }
              },
              {
                path: 'GroupMembership',
                lazy: async () => {
                  const { default: PageReportsGroupMembership } = await import(
                    './screens/reports/group-membership/Page'
                  )
                  return { Component: PageReportsGroupMembership }
                }
              },
              {
                path: 'ChangeRequests',
                lazy: async () => {
                  const { default: PageReportsWorkflows } = await import('./screens/reports/workflows/Page')
                  return { Component: PageReportsWorkflows }
                }
              },
              {
                path: 'ChangeRequestsTypes',
                lazy: async () => {
                  const { default: PageReportsWorkflowTemplates } = await import(
                    './screens/reports/workflow-templates/Page'
                  )
                  return { Component: PageReportsWorkflowTemplates }
                }
              },
              {
                path: 'PropertiesUsage',
                lazy: async () => {
                  const { default: PageReportsPropertiesUsage } = await import(
                    './screens/reports/properties-usage/Page'
                  )
                  return { Component: PageReportsPropertiesUsage }
                }
              },
              {
                path: 'Tasks',
                lazy: async () => {
                  const { default: PageReportsTasks } = await import('./screens/reports/tasks/Page')
                  return { Component: PageReportsTasks }
                }
              },
              {
                path: 'Rejections',
                lazy: async () => {
                  const { default: PageReportsRejectionReasons } = await import(
                    './screens/reports/rejection-reasons/Page'
                  )
                  return { Component: PageReportsRejectionReasons }
                }
              },
              {
                path: 'DocExpiration',
                lazy: async () => {
                  const { default: PageReportsDocExpiration } = await import('./screens/reports/doc-expiration/Page')
                  return { Component: PageReportsDocExpiration }
                }
              },
              {
                path: 'Custom',
                lazy: async () => {
                  const { default: PageReportsCustom } = await import('./screens/reports/custom/Page')
                  return { Component: PageReportsCustom }
                }
              },
              {
                path: 'eTMFCompletion',
                lazy: async () => {
                  const { default: EtmfCompletionRatePage } = await import('./screens/reports/etmf-completion-rate')
                  return { Component: EtmfCompletionRatePage }
                }
              },
              {
                path: 'eTMFContributorDocStatus',
                lazy: async () => {
                  const { default: EtmfContributorDocStatusPage } = await import(
                    './screens/reports/etmf-contributor-doc-status'
                  )
                  return { Component: EtmfContributorDocStatusPage }
                }
              },
              {
                path: 'eTMFDocDetail',
                lazy: async () => {
                  const { default: EtmfDocDetailsPage } = await import('./screens/reports/etmf-doc-details')
                  return { Component: EtmfDocDetailsPage }
                }
              },
              {
                path: 'eTMFFiledDocs',
                lazy: async () => {
                  const { default: EtmfFiledDocsPage } = await import('./screens/reports/etmf-filed-docs')
                  return { Component: EtmfFiledDocsPage }
                }
              },
              {
                path: 'PropertySearch',
                lazy: async () => {
                  const { default: ReportPropertySearch } = await import('./screens/reports/property-search/index')
                  return { Component: ReportPropertySearch }
                }
              },
              {
                path: 'QcReview',
                lazy: async () => {
                  const { default: QCReviewReport } = await import('./screens/reports/qc-review/index')
                  return { Component: QCReviewReport }
                }
              },
              {
                path: 'duplicateDocuments',
                lazy: async () => {
                  const { default: PageReportDuplicateDocument } = await import(
                    './screens/reports/duplicate-documents/index'
                  )
                  return { Component: PageReportDuplicateDocument }
                }
              }
            ]
          },
          {
            path: 'EosStudyOverview',
            element: <EosOverviewPage />
          },
          {
            path: 'EosStudyOverviewLegacy',
            element: <EosOverviewPage isLegacy />
          },
          {
            path: 'settings',
            element: <MyRaveRCMSettings />
          },
          {
            path: 'settings/:settingsTab',
            element: <Settings />
          },
          {
            path: 'settings/activity',
            lazy: async () => {
              const { ActivityPage } = await import('./screens/reports/activity')
              return { Component: ActivityPage }
            }
          },
          {
            path: 'MySavedReportSettings',
            lazy: async () => {
              const { default: PageSavedReportSetting } = await import('./screens/savedReportSetting/Page')
              const { loader } = await import('./routes/saved-report-settings')
              return { Component: PageSavedReportSetting, loader }
            }
          },
          {
            path: 'system',
            element: <System />,
            children: [
              {
                index: true,
                lazy: async () => {
                  const { default: SystemInformation } = await import('./screens/system/SystemInformation')
                  return { Component: SystemInformation }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.PERFORMANCE,
                lazy: async () => {
                  const { default: SystemInformation } = await import('./screens/system/SystemInformation')
                  return { Component: SystemInformation }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.POLICIES,
                lazy: async () => {
                  const { default: Policies } = await import('./screens/system/policies')
                  return { Component: Policies }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.ETMF_SETUP,
                lazy: async () => {
                  const { default: ClinicalSetup } = await import('./screens/system/clinical-setup')
                  return { Component: ClinicalSetup }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.TEXT_MESSAGES,
                lazy: async () => {
                  const { default: TextMessages } = await import('./screens/system/text-messages')
                  return { Component: TextMessages }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.INTEGRATIONS,
                lazy: async () => {
                  const { default: Integrations } = await import('./screens/system/integrations')
                  return { Component: Integrations }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.OPERATING_MODE,
                lazy: async () => {
                  const { default: OperatingMode } = await import('./screens/system/operating-mode')
                  return { Component: OperatingMode }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.USERS,
                lazy: async () => {
                  const { default: Users } = await import('./screens/system/users')
                  return { Component: Users }
                }
              },
              {
                path: ROUTE_PATHS.SYSTEM.GROUPS,
                lazy: async () => {
                  const { default: Groups } = await import('./screens/system/groups')
                  return { Component: Groups }
                }
              }
            ]
          },
          {
            path: 'staging',
            element: <StagingLayout />,
            children: [
              { index: true, element: <Navigate to="overview" replace /> },
              { path: 'overview', element: <StagingOverviewPage /> },
              { path: 'MissingDocuments', element: <PageStagingDocs key="MissingDocuments" /> },
              { path: 'MyDocuments', element: <PageStagingDocs key="MyDocuments" /> },
              { path: 'Documents', element: <SiteDocsPage /> },
              { path: 'StudyDocuments', element: <PageStagingDocs key="StudyDocuments" /> },
              { path: 'property-search', element: <PageStagingDocs key="property-search" /> },
              { path: 'stagingarea', element: <StagingArea /> }
              // { path: 'upload-docs', element: <UploadDocsPage /> } //update doc modal (no mapping required?)
            ]
          },
          {
            path: 'users/:userId/activity',
            element: (
              <div className="mcc-content-frame theme-sandman">
                <div className="system-container">
                  <Suspense fallback={<LoadingSpinner />}>
                    <UserActivity />
                  </Suspense>
                </div>
              </div>
            )
          },
          //FsiActivity/1763601
          {
            path: 'FsiActivity/:id',
            element: <FsiActivity />
          }
        ]
      },
      {
        path: ROUTE_PATHS.LOGIN,
        lazy: async () => {
          const { default: Login, loader } = await import('./routes/login')
          return { Component: Login, loader }
        }
      },
      {
        path: ROUTE_PATHS.NEW_ACCOUNT,
        lazy: async () => {
          const { default: NewAccount, loader } = await import('./routes/new-account.$hash')
          return { Component: NewAccount, loader }
        }
      },
      {
        path: ROUTE_PATHS.RESET_PASSWORD,
        lazy: async () => {
          const { default: NewAccount, loader } = await import('./routes/new-account.$hash')
          return { Component: NewAccount, loader }
        }
      },
      {
        path: ROUTE_PATHS.FORGOT_PASSWORD,
        lazy: async () => {
          const { default: ForgotPassword, loader } = await import('./routes/forgot-password')
          return { Component: ForgotPassword, loader }
        }
      },
      { path: ROUTE_PATHS.NOT_FOUND, element: <NoMatch /> }
    ],
    isHashRouting
      ? { basename: '/!/app' } // For hash routing: #!/app/Home → strips #!/app → matches /Home
      : { basename: '/app' } // For browser routing: /app/Home → strips /app → matches /Home
  )

  console.log('🔧 [Router Init] Router created successfully')
  console.log('🔧 [Router Init] Router state:', router.state)
  console.log('🔧 [Router Init] Mounting RouterProvider...')

  const rootElement = document.getElementById('root')
  if (rootElement) {
    ReactDOM.createRoot(rootElement).render(
      <I18nextProvider i18n={i18nInstance}>
        <RouterProvider router={router} />
      </I18nextProvider>
    )
    console.log('🔧 [Router Init] RouterProvider mounted')
  } else {
    console.error('🔧 [Router Init] ERROR: Root element not found!')
  }
}

initApp()
